import {
  F0,
  F1,
  TypedOmit,
  TypedValue,
  ValueOf,
} from '@client/new/helpers/types';
import { Typography } from '@design-system/components/typography/typography';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProps } from '@mui/material';
import React from 'react';
import * as cs from './ContentTable.css';
import { Link } from '@client/router/compat';
import { Sorting, SortingOrder } from './useSorting';
import { Pill } from '@client/new/pages/promotions/view/components/Pill';
import { color } from '@design-system/tokens';

export const text = (value: string): ContentType => ({
  type: 'text',
  value,
});

export const pill = (value: ValueOf<ContentType, 'pill'>): ContentType => ({
  type: 'pill',
  value,
});

export const button = (value: {
  content: string;
  onClick: F0;
}): ContentType => ({ type: 'button', value });

export const link = (value: { content: string; to: string }): ContentType => ({
  type: 'link',
  value,
});

export const row = (value: ValueOf<RowType, 'row'>): RowType => ({
  type: 'row',
  value,
});

export type RowType = TypedValue<
  'row',
  { cells: Array<ContentType>; id: string }
>;

export type ContentType =
  | TypedValue<'text', string>
  | TypedValue<'icon', IconProps>
  | TypedValue<'pill', { content: string; backgroundColor: keyof typeof color }>
  | TypedValue<'button', { content: string; onClick: F0 }>
  | TypedValue<'link', { content: string; to: string }>;

type HeaderColType = TypedValue<
  'header-col',
  {
    width?: string;
    align?: 'left' | 'center' | 'right';
    content?: string;
    arrow?: SortingOrder;
    name?: string;
  }
>;
export const headerColValue = (
  p: ValueOf<HeaderColType, 'header-col'>,
): TypedOmit<ValueOf<HeaderColType, 'header-col'>, 'name'> => ({
  width: p.width || 'auto',
  content: p.content,
  align: p.align,
  arrow: p.arrow,
});
export const headerCol = (
  p: ValueOf<HeaderColType, 'header-col'>,
): HeaderColType => ({
  type: 'header-col',
  value: {
    name: p.name,
    width: p.width || 'auto',
    content: p.content,
    align: p.align,
    arrow: p.arrow,
  },
});

const toGridTemplate = (cols: Array<HeaderColType>) =>
  cols.map((col) => col.value.width).join(' ');

type STableHeaderProps<T extends string = string> = {
  data: Array<HeaderColType>;
  sortedCol: string | null;
  sortedOrder: SortingOrder | null;
  onHeaderClick: F1<T | null>;
};

const HeaderCol = (
  p: HeaderColType & {
    isSortable: boolean;
    onClick: F0;
    sortedOrder: SortingOrder | null;
  },
) => (
  <div
    className={cs.headerCol}
    style={{
      justifyContent: p.value.align || 'center',
      cursor: p.isSortable ? 'pointer' : 'auto',
    }}
    onClick={() => {
      if (p.isSortable) p.onClick();
    }}
  >
    <Typography variant="body" size="medium" fontWeight="medium">
      {p.value.content || ''}
    </Typography>
    {p.sortedOrder === null ? null : (
      <FontAwesomeIcon
        icon={p.sortedOrder === 'up' ? faArrowUp : faArrowDown}
        size={'xs'}
        style={{ marginTop: -2 }}
      />
    )}
  </div>
);

const HeaderRow = <T extends string>(p: STableHeaderProps<T>) => (
  <div
    className={cs.headerRow}
    style={{
      gridTemplateColumns: toGridTemplate(p.data),
    }}
  >
    {p.data.map((col, i) => {
      const isSortable =
        Boolean(col.value.name) && p.onHeaderClick !== undefined;
      return (
        <HeaderCol
          {...col}
          key={i}
          isSortable={isSortable}
          sortedOrder={col.value.name === p.sortedCol ? p.sortedOrder : null}
          onClick={() => p.onHeaderClick((col.value.name as T) || null)}
        />
      );
    })}
  </div>
);

const ContentCol = (
  p: ContentType & { headerCol: HeaderColType },
): React.ReactNode => {
  const content = () => {
    switch (p.type) {
      case 'text':
        return (
          <Typography variant="body" size="large" fontWeight="normal">
            {p.value}
          </Typography>
        );
      case 'button':
        return (
          <Typography
            variant="body"
            size="large"
            onClick={p.value.onClick}
            fontWeight="bold"
            color="P-80"
            style={{ cursor: 'pointer' }}
          >
            {p.value.content}
          </Typography>
        );
      case 'link':
        return (
          <Link to={p.value.to}>
            <Typography
              variant="body"
              size="large"
              fontWeight="bold"
              color="P-80"
              style={{ cursor: 'pointer' }}
            >
              {p.value.content}
            </Typography>
          </Link>
        );
      case 'pill':
        return <Pill type="Active" {...p.value} />;
    }
  };

  return (
    <div
      className={cs.contentCol}
      style={{
        width: '100%',

        justifyContent: p.headerCol.value.align || 'center',
      }}
    >
      {content()}
    </div>
  );
};

const ContentRow = (p: { data: RowType; header: Array<HeaderColType> }) => (
  <div
    className={cs.contentRow}
    style={{
      gridTemplateColumns: toGridTemplate(p.header),
    }}
  >
    {p.data.value.cells.map((col, i) => (
      <ContentCol key={i} {...col} headerCol={p.header[i]} />
    ))}
  </div>
);

const ContentRows = (p: {
  data: Array<RowType>;
  header: Array<HeaderColType>;
}) => (
  <div>
    {p.data.map((row, i) => (
      <ContentRow key={i} data={row} header={p.header} />
    ))}
  </div>
);

type ContentTableProps<T extends string = string> = {
  header: Array<HeaderColType>;
  rows: Array<RowType>;
} & Sorting<T>;

export const ContentTable = <T extends string = string>(
  p: ContentTableProps<T>,
) => (
  <>
    <HeaderRow
      data={p.header}
      onHeaderClick={p.setColumn}
      sortedCol={p.column}
      sortedOrder={p.order}
    />
    <ContentRows data={p.rows} header={p.header} />
  </>
);
